import { Close, Minimize } from '@ankr.com/ui';
import {
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogProps as MuiDialogProps,
  DialogTitle as MuiDialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

import { useThemes } from 'uiKit/Theme/hook/useThemes';

import { useStyles } from './DialogStyles';
import { DialogTitleColor } from './types';
import { useDialog } from './useDialog';

/**
 * It equals to 0 because when we close the modal the context resets to undefined.
 * If animation would be used user will see blinking of the modal content.
 */
const EXIT_ANIMATION_TIMEOUT = 0;

export type IDialogProps = Omit<
  MuiDialogProps,
  'BackdropProps' | 'title' | 'onClose'
> & {
  canCloseDialogByClickOutside?: boolean;
  closeButtonClassName?: string;
  dialogContentClassName?: string;
  hasMinimizeIcon?: boolean;
  hasTitleWrapper?: boolean;
  initialTitle?: string;
  isHidden?: boolean;
  maxPxWidth?: number;
  onClose?: () => void;
  onManualClose?: () => void;
  containerClassName?: string;
  paperClassName?: string;
  shouldHideCloseButton?: boolean;
  shouldStopPropagationOnClose?: boolean;
  title?: ReactNode;
  titleClassName?: string;
  /**
   * It indicates that the modal will use redux data. This parameter disables the modal closing animation.
   */
  withContext?: boolean;
};

export const Dialog = ({
  PaperProps,
  TransitionProps,
  canCloseDialogByClickOutside = true,
  children,
  closeButtonClassName,
  containerClassName,
  dialogContentClassName,
  hasMinimizeIcon,
  hasTitleWrapper = true,
  initialTitle = '',
  isHidden = false,
  maxPxWidth,
  onClose,
  onManualClose,
  paperClassName,
  shouldHideCloseButton = false,
  shouldStopPropagationOnClose,
  title,
  titleClassName,
  withContext,
  ...props
}: IDialogProps) => {
  const { dialogTitle, handleClose, handleEventBubbling, onCloseIconClick } =
    useDialog({
      initialTitle,
      title,
      onClose,
      onManualClose,
      shouldStopPropagationOnClose,
    });

  const { isLightTheme } = useThemes();

  const { classes, cx } = useStyles({
    dialogTitleColor: dialogTitle.color || DialogTitleColor.Regular,
    isHidden,
    isLightTheme,
    maxPxWidth,
  });

  return (
    <MuiDialog
      TransitionProps={
        withContext
          ? { timeout: { exit: EXIT_ANIMATION_TIMEOUT } }
          : TransitionProps
      }
      PaperProps={{
        ...PaperProps,
        // disable propagation to prevent routing on event bubbling
        onClick: handleEventBubbling,
        classes: {
          root: cx(classes.paper, paperClassName),
        },
      }}
      BackdropProps={{
        classes: {
          root: classes.backdrop,
        },
      }}
      {...props}
      classes={{
        ...props.classes,
        root: cx(classes.root, props.classes?.root),
        container: containerClassName,
      }}
      onClose={canCloseDialogByClickOutside ? handleClose : undefined}
    >
      {(dialogTitle.title || !shouldHideCloseButton) && (
        <MuiDialogTitle className={cx(classes.dialogTitle, titleClassName)}>
          {typeof dialogTitle.title === 'string' && hasTitleWrapper ? (
            <Typography
              className={cx(classes.titleText, {
                [classes.titleWithPaddingRight]: !shouldHideCloseButton,
              })}
            >
              {dialogTitle.title}
            </Typography>
          ) : (
            dialogTitle.title
          )}
          {!shouldHideCloseButton && (
            <IconButton
              aria-label="close"
              className={cx(classes.closeButton, closeButtonClassName)}
              onClick={onCloseIconClick}
            >
              {hasMinimizeIcon ? <Minimize /> : <Close />}
            </IconButton>
          )}
        </MuiDialogTitle>
      )}
      <MuiDialogContent
        className={cx(classes.dialogContent, dialogContentClassName)}
      >
        {children}
      </MuiDialogContent>
    </MuiDialog>
  );
};
